// utils/processMeasurements.js
import { supabase } from '../supabaseClient';
import proj4 from 'proj4';

/**
 * Fixed mapping of column index → sanitized field key
 * (matches your “Proj Name;Location;Location;…;Voids” order)
 */
const HEADER_KEYS = [
  'ProjName',     //  0
  'Location1',    //  1
  'Location2',    //  2
  'Contact',      //  3
  'MixName',      //  4
  'StoneSize',    //  5
  'Depth',        //  6
  'MTD',          //  7
  'Offset',       //  8
  'GaugeOp',      //  9
  'Station',      // 10
  'Description1', // 11
  'Description2', // 12
  'Operator',     // 13
  'Density',      // 14
  'Compaction',   // 15
  'SurfTemp',     // 16
  'GPS',          // 17  ← strip quotes
  'GPSTime',      // 18
  'DateTime',     // 19
  'Voids',        // 20
];

/**
 * Parse a .pqidat (PQI380) file into an array of records,
 * *always* using the above index mapping, regardless of language.
 * - Drops the first (version) line
 * - Skips the header row entirely (we know the order)
 * - Splits on `;`
 * - Pads/truncates to exactly 21 columns
 * - Trims each value; treats empty string as null
 * - Removes surrounding quotes from GPS column
 */
export async function parsePqidatFile(file) {
  const text = await file.text();
  const lines = text.split(/\r?\n/);

    // 1) grab+drop firmware/version line and extract serial
    const versionLine = lines.shift();
    const versionCols = versionLine.split(';');
    const gaugeId = versionCols[1]?.trim() || null;
    console.log('Gauge ID:', gaugeId);
  
    // 2) drop header row
    if (lines.length === 0) {
      throw new Error('Empty file after dropping version line');
    }
    lines.shift();
  

  const records = [];
  for (let ln of lines) {
    if (!ln.trim()) continue;
    // split
    let cols = ln.split(';');
    // pad with nulls
    while (cols.length < HEADER_KEYS.length) cols.push(null);
    // truncate extras
    if (cols.length > HEADER_KEYS.length) {
      cols = cols.slice(0, HEADER_KEYS.length);
    }

    const rec = {};
    HEADER_KEYS.forEach((key, idx) => {
      let v = cols[idx] == null ? null : cols[idx].trim();
      if (v === '') v = null;

      // strip quotes only on the GPS field
      if (key === 'GPS' && typeof v === 'string') {
        v = v.replace(/^"(.*)"$/, '$1');
      }

      rec[key] = v;
    });

    rec.gauge_id = gaugeId;

    records.push(rec);
  }

  return records;
}

export const analyzeFileData = (pqidatRecords) => {
  let recordsWithGPS = 0;
  let recordsWithoutGPS = 0;
  let invalidDateTime = 0;

  pqidatRecords.forEach(record => {
    const dbTime = formatLocalToDb(record.DateTime);
    if (!dbTime) {
      invalidDateTime++;
      return;
    }

    const rawCoords = record.GPS ? convertGpsToLatLng(record.GPS) : null;
    if (rawCoords) {
      recordsWithGPS++;
    } else {
      recordsWithoutGPS++;
    }
  });

  return {
    total: pqidatRecords.length,
    recordsWithGPS,
    recordsWithoutGPS,
    invalidDateTime,
    hasGPSData: recordsWithGPS > 0,
    hasValidData: (recordsWithGPS + recordsWithoutGPS) > 0
  };
};

/**
 * Convert PQI "DateTime" like "MM/DD/YY hh:mm" or "MM/DD/YYYY hh:mm:ss"
 * into the DB's format "YYYY-MM-DDTHH:MM:SS"
 */
const formatLocalToDb = (rawDate) => {
  if (!rawDate) return null;
  const [datePart, timePart] = rawDate.split(' ');
  if (!datePart || !timePart) return null;

  // split date and normalize year
  const [MMs, DDs, Ys] = datePart.split('/');
  if (!MMs || !DDs || !Ys) return null;
  const MM = Number(MMs), DD = Number(DDs), Y  = Number(Ys);
  // if two‑digit, assume 20YY; otherwise leave as‑is
  const year = Y < 100 ? 2000 + Y : Y;

  // split time (may include seconds)
  const [hs, ms, ss = '0'] = timePart.split(':');
  const hh = Number(hs), mm = Number(ms), sec = Number(ss);

  // zero‑pad each component
  const m2 = String(MM).padStart(2,'0');
  const d2 = String(DD).padStart(2,'0');
  const h2 = String(hh).padStart(2,'0');
  const mi = String(mm).padStart(2,'0');
  const s2 = String(sec).padStart(2,'0');

  return `${year}-${m2}-${d2}T${h2}:${mi}:${s2}`;
};

/**
 * Normalize a "lat,lng" string to 3 decimal places.
 * E.g. "41.2025635,-87.1964681" → "41.203,-87.196"
 */
const normalizeCoords = (coord) => {
  if (!coord) return null;
  const [latStr, lonStr] = coord.split(',').map((s) => s.trim());
  const lat = parseFloat(latStr).toFixed(3);
  const lon = parseFloat(lonStr).toFixed(3);
  return `${lat},${lon}`;
};

/**
 * Does this look like a UTM zone+band?
 * e.g. "16S", "18T", "33V", etc.
 */
const isUtmString = (zoneBand) =>
  /^\d{1,2}[C-HJ-NP-X]$/i.test(zoneBand);

/**
 * Convert a UTM string like "16S 513,928E 3,872,194N"
 * into decimal "lat,lng" using proj4.
 */
const convertUtmToLatLng = (utmStr) => {
  const [zoneBand, eastingRaw, northingRaw] = utmStr.trim().split(/\s+/);
  const zone = parseInt(zoneBand, 10);
  const band = zoneBand.slice(-1).toUpperCase();
  // bands A–M are southern hemisphere
  const south = band < 'N';

  const easting  = parseInt(eastingRaw.replace(/[^\d]/g, ''), 10);
  const northing = parseInt(northingRaw.replace(/[^\d]/g, ''), 10);

  const utmProj = `+proj=utm +zone=${zone} ${south ? '+south' : ''} +datum=WGS84 +units=m +no_defs`;
  const [lon, lat] = proj4(utmProj, 'EPSG:4326', [easting, northing]);
  return `${lat},${lon}`;
};

/**
 * Convert a PQI GPS string in DMS format
 * e.g. "41 12.15088 N 87 11.84277 W"
 */
const convertDmsToLatLng = (gps) => {
  const parts = gps.trim().split(/\s+/);
  if (parts.length !== 6) {
    throw new Error(`Invalid GPS format: ${gps}`);
  }
  const [latDegStr, latMinStr, latDir, lonDegStr, lonMinStr, lonDir] = parts;
  let lat = parseFloat(latDegStr) + parseFloat(latMinStr) / 60;
  let lng = parseFloat(lonDegStr) + parseFloat(lonMinStr) / 60;
  if (latDir.toUpperCase() === 'S') lat = -lat;
  if (lonDir.toUpperCase() === 'W') lng = -lng;
  return `${lat},${lng}`;
};

/**
 * Unified converter: detect UTM vs DMS & dispatch
 */
const convertGpsToLatLng = (gps) => {
  if (!gps) return null;
  const trimmed = gps.trim();
  const firstToken = trimmed.split(/\s+/)[0];
  if (isUtmString(firstToken)) {
    return convertUtmToLatLng(trimmed);
  } else {
    return convertDmsToLatLng(trimmed);
  }
};

/**
 * Fetch existing measurements for the user's organization,
 * returning both GpsTime and GpsCoordinates.
 */
export const fetchOrganizationMeasurements = async (userId) => {
  try {
    const { data: orgMember, error: e1 } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', userId)
      .single();
    if (e1 || !orgMember) throw new Error('User not in an organization');

    const { data: members, error: e2 } = await supabase
      .from('organization_members')
      .select('user_id')
      .eq('organization_id', orgMember.organization_id);
    if (e2) throw e2;

    const memberIds = members.map((m) => m.user_id);
    const { data, error: e3 } = await supabase
      .from('measurements')
      .select('GpsTime, GpsCoordinates')
      .in('profile_id', memberIds);
    if (e3) throw e3;

    return data;
  } catch (err) {
    throw new Error(`Failed to fetch organization measurements: ${err.message}`);
  }
};

/**
 * Only keep PQI records whose composite key
 * (DateTime + normalized coords) is not already in the DB.
 */
export const findNewRecords = (pqidatRecords, existingMeasurements) => {
  const existingSet = new Set();
  
  // Create composite keys for existing measurements
  existingMeasurements.forEach((m) => {
    if (m.GpsTime) {
      if (m.GpsCoordinates) {
        // GPS-based key: "TIME|GPS"
        existingSet.add(`${m.GpsTime}|${normalizeCoords(m.GpsCoordinates)}`);
      } else {
        // Time-only key for records without GPS: "TIME|NO_GPS"
        existingSet.add(`${m.GpsTime}|NO_GPS`);
      }
    }
  });

  const results = {
    newRecords: [],
    recordsWithGPS: 0,
    recordsWithoutGPS: 0,
    duplicatesFound: 0
  };

  const filteredRecords = pqidatRecords.filter((record) => {
    const dbTime = formatLocalToDb(record.DateTime);
    if (!dbTime) return false; // Skip records with invalid time
    
    const rawCoords = record.GPS ? convertGpsToLatLng(record.GPS) : null;
    const coordsNorm = normalizeCoords(rawCoords);
    
    let key;
    if (coordsNorm) {
      // Has GPS coordinates
      key = `${dbTime}|${coordsNorm}`;
      results.recordsWithGPS++;
    } else {
      // No GPS - use time-only key
      key = `${dbTime}|NO_GPS`;
      results.recordsWithoutGPS++;
    }
    
    if (existingSet.has(key)) {
      results.duplicatesFound++;
      return false; // Duplicate found
    }
    
    return true; // New record
  });

  results.newRecords = filteredRecords;
  return results;
};

/**
 * Upload new PQI records into Supabase,
 * writing formatted DateTime to both GpsTime and LocalTimeOfMeasurement,
 * and full-precision coords to GpsCoordinates.
 */
export const uploadNewRecords = async (records, userId, projectId = null) => {
  try {
    const toInsert = records.map((r) => {
      const dbTime     = formatLocalToDb(r.DateTime);
      const localIso   = r.DateTime ? new Date(r.DateTime).toISOString() : null;
      const coordsFull = r.GPS ? convertGpsToLatLng(r.GPS) : null;

      return {
        profile_id:             userId,
        projectdescription:     r.ProjName           || null,
        Location1:              r.Location1          || null,
        Location2:              r.Location2          || null,
        Contact:                r.Contact            || null,
        Station:                r.Station            || null,
        Description1:           r.Description1       || null,
        Description2:           r.Description2       || null,
        Operator:               r.Operator           || null,
        Density:                parseFloat(r.Density)    || null,
        Compaction:             parseFloat(r.Compaction) || null,
        SurfaceTemp:            parseFloat(r.SurfTemp)   || null,
        Voids:                  parseFloat(r.Voids)      || null,
        GpsCoordinates:         coordsFull,
        GpsTime:                dbTime,
        LocalTimeOfMeasurement: localIso,
        gauge_type:             r.gauge_id     || null,
        MixName:                r.MixName           || null,
      };
    });

    const { data, error } = await supabase
      .from('measurements')
      .insert(toInsert)
      .select();
    if (error) throw error;

    if (projectId && data.length) {
      const mappings = data.map((m) => ({
        measurement_id: m.id,
        project_id:     projectId,
      }));
      const { error: mapErr } = await supabase
        .from('project_mapping')
        .insert(mappings);
      if (mapErr) {
        // rollback on failure
        await supabase
          .from('measurements')
          .delete()
          .in('id', data.map((m) => m.id));
        throw mapErr;
      }
    }

    return data;
  } catch (err) {
    throw new Error(`Failed to upload measurements: ${err.message}`);
  }
};
